import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchTranscriptions, } from '@/entities/transcription-job';
import { ApiError } from '@/shared/api';
import { transcriptionStatusLabel, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Badge, Button, DirectoryPanel, SearchField, Skeleton, StageTabs, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/shared/ui';
const FILTERS = [
    { id: 'all', label: 'Все', countKey: 'all' },
    { id: 'ready', label: 'Готовые', countKey: 'ready' },
    { id: 'in_progress', label: 'В процессе', countKey: 'inProgress' },
    { id: 'failed', label: 'Ошибка', countKey: 'failed' },
    { id: 'cancelled', label: 'Отменены', countKey: 'cancelled' },
];
function parseFilter(raw) {
    if (raw === 'ready' ||
        raw === 'in_progress' ||
        raw === 'failed' ||
        raw === 'cancelled' ||
        raw === 'all') {
        return raw;
    }
    return 'all';
}
function normalize(value) {
    return value.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е');
}
function pluralTranscriptions(count) {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'расшифровка';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return 'расшифровки';
    }
    return 'расшифровок';
}
function formatClock(date) {
    return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
    });
}
function formatWhen(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return iso;
    }
    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
function formatPhone(raw) {
    const digits = raw.replace(/\D/g, '');
    const ten = digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))
        ? digits.slice(1)
        : digits;
    if (ten.length !== 10) {
        return raw;
    }
    return `+7 ${ten.slice(0, 3)} ${ten.slice(3, 6)}-${ten.slice(6, 8)}-${ten.slice(8)}`;
}
function callParts(item) {
    const parts = [];
    if (item.callOccurredAt) {
        parts.push(formatWhen(item.callOccurredAt));
    }
    if (item.callDirection === 'OUTBOUND') {
        parts.push('Исходящий');
    }
    else if (item.callDirection === 'INBOUND') {
        parts.push('Входящий');
    }
    if (item.callPhone) {
        parts.push(formatPhone(item.callPhone));
    }
    return parts;
}
function durationLabel(seconds) {
    if (seconds == null || seconds < 0) {
        return null;
    }
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    if (minutes === 0) {
        return `${rest} с`;
    }
    return `${minutes} мин ${rest} с`;
}
function emptyCopy(filter) {
    if (filter === 'ready') {
        return {
            title: 'Готовых расшифровок пока нет',
            hint: 'Когда разговор расшифруется, текст появится здесь.',
        };
    }
    if (filter === 'in_progress') {
        return {
            title: 'Сейчас ничего не расшифровываем',
            hint: 'Откройте звонок в очереди — расшифровка начнётся сама.',
        };
    }
    if (filter === 'failed') {
        return {
            title: 'Ошибок расшифровки нет',
            hint: 'Если расшифровка не получится, она будет в этом списке.',
        };
    }
    if (filter === 'cancelled') {
        return {
            title: 'Отменённых расшифровок нет',
            hint: 'Отменённые задания появятся здесь.',
        };
    }
    return {
        title: 'Расшифровок пока нет',
        hint: 'Откройте карточку агента в очередях и посмотрите звонки — расшифровка начнётся сама.',
    };
}
function isFixtureTranscriptionText(text) {
    return Boolean(text?.startsWith('Stub-расшифровка звонка'));
}
function statusTone(item, fixture) {
    if (fixture || item.status === 'queued' || item.status === 'processing') {
        return 'info';
    }
    if (item.status === 'ready')
        return 'success';
    if (item.status === 'failed')
        return 'warning';
    return 'neutral';
}
function matchesQuery(item, query) {
    if (!query)
        return true;
    const hay = normalize([
        item.agentDisplayName,
        item.callPhone ?? '',
        item.text ?? '',
        item.errorMessage ?? '',
        callParts(item).join(' '),
    ].join(' '));
    return hay.includes(normalize(query));
}
function TranscriptionText({ item }) {
    const fixture = isFixtureTranscriptionText(item.text);
    const inProgress = item.status === 'queued' || item.status === 'processing' || fixture;
    if (inProgress) {
        return _jsx("p", { className: "text-muted-foreground", children: "\u0420\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u044B\u0432\u0430\u0435\u043C \u0440\u0430\u0437\u0433\u043E\u0432\u043E\u0440\u2026" });
    }
    if (item.status === 'failed') {
        return (_jsx("p", { className: "text-destructive", children: item.errorMessage ?? 'Не получилось расшифровать разговор' }));
    }
    if (item.status === 'ready' && item.text && !fixture) {
        return (_jsxs("details", { children: [_jsx("summary", { className: "cursor-pointer font-medium text-primary", children: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0442\u0435\u043A\u0441\u0442" }), _jsx("p", { className: "mt-2 whitespace-pre-wrap text-foreground", children: item.text })] }));
    }
    if (item.status === 'ready' && !item.text) {
        return _jsx("p", { className: "text-muted-foreground", children: "\u0422\u0435\u043A\u0441\u0442 \u0435\u0449\u0451 \u043D\u0435 \u043F\u0440\u0438\u0448\u0451\u043B." });
    }
    if (item.status === 'cancelled') {
        return _jsx("p", { className: "text-muted-foreground", children: "\u0420\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u043A\u0443 \u043E\u0442\u043C\u0435\u043D\u0438\u043B\u0438." });
    }
    return _jsx("span", { className: "text-muted-foreground", children: "\u2014" });
}
export function TranscriptionList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = parseFilter(searchParams.get('status'));
    const query = searchParams.get('q') ?? '';
    const [draftQ, setDraftQ] = useState(query);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(async (showLoading) => {
        if (showLoading) {
            setLoading(true);
        }
        else {
            setRefreshing(true);
        }
        setError(null);
        try {
            const next = await fetchTranscriptions(filter);
            setData(next);
            setCheckedAt(new Date());
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
        }
        finally {
            if (showLoading) {
                setLoading(false);
            }
            setRefreshing(false);
        }
    }, [filter]);
    useEffect(() => {
        void load(true);
    }, [load]);
    useEffect(() => {
        const hasOpen = data?.items.some((item) => item.status === 'queued' || item.status === 'processing');
        const delay = hasOpen || filter === 'in_progress' ? 8000 : 20000;
        const timer = window.setInterval(() => {
            void load(false);
        }, delay);
        return () => window.clearInterval(timer);
    }, [data?.items, filter, load]);
    useEffect(() => {
        setDraftQ(query);
    }, [query]);
    const writeQuery = useCallback((next) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (!next)
                params.delete('q');
            else
                params.set('q', next);
            return params;
        }, { replace: true });
    }, [setSearchParams]);
    useEffect(() => {
        if (draftQ.trim() === query)
            return;
        const timer = window.setTimeout(() => writeQuery(draftQ.trim()), 250);
        return () => window.clearTimeout(timer);
    }, [draftQ, query, writeQuery]);
    function setFilter(next) {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (!next || next === 'all')
                params.delete('status');
            else
                params.set('status', next);
            return params;
        }, { replace: true });
    }
    function resetFilters() {
        setDraftQ('');
        setSearchParams({}, { replace: true });
    }
    const counts = data?.counts;
    const items = data?.items ?? [];
    const rows = useMemo(() => items.filter((item) => matchesQuery(item, query)), [items, query]);
    const stageTabItems = useMemo(() => FILTERS.map((item) => ({
        id: item.id,
        label: item.label,
        count: counts?.[item.countKey],
    })), [counts]);
    const empty = emptyCopy(filter);
    const showSkeleton = loading && !data;
    const filtersActive = query.length > 0 || filter !== 'all';
    const footer = refreshing
        ? 'Проверяем список расшифровок.'
        : checkedAt
            ? `Список проверен ${formatClock(checkedAt)}. Новый звонок появится после открытия карточки агента в очереди.`
            : 'Новый звонок появится после открытия карточки агента в очереди.';
    return (_jsxs("div", { className: "space-y-3", children: [_jsx(StageTabs, { items: stageTabItems, value: filter, onValueChange: setFilter, "aria-label": "\u0421\u0442\u0430\u0442\u0443\u0441 \u0440\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u043A\u0438" }), error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(true), children: uiCopy.retry })] })) : null, _jsx(DirectoryPanel, { total: _jsxs(_Fragment, { children: [_jsx("strong", { children: rows.length }), " ", pluralTranscriptions(rows.length)] }), actions: filtersActive ? (_jsx("button", { type: "button", className: "text-[11px] text-primary hover:underline", onClick: resetFilters, children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C" })) : null, search: _jsx(SearchField, { className: "w-full", value: draftQ, onValueChange: setDraftQ, placeholder: "\u0410\u0433\u0435\u043D\u0442, \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u0438\u043B\u0438 \u0442\u0435\u043A\u0441\u0442", "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0440\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u043E\u043A" }), footer: footer, children: showSkeleton ? (_jsx(Skeleton, { className: "h-64 w-full rounded-none" })) : error && items.length === 0 ? null : !error && items.length === 0 ? (_jsx("div", { className: "px-4 py-6", children: _jsxs(Alert, { children: [_jsx(AlertTitle, { children: empty.title }), _jsx(AlertDescription, { children: empty.hint })] }) })) : rows.length === 0 ? (_jsx("p", { className: "px-4 py-6 text-sm text-muted-foreground", children: "\u0421\u0442\u0440\u043E\u043A \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E." })) : (_jsxs(Table, { density: "compact", framed: false, "aria-label": "\u0420\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u043A\u0438", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { children: "\u0410\u0433\u0435\u043D\u0442" }), _jsx(TableHead, { children: "\u0421\u0442\u0430\u0442\u0443\u0441" }), _jsx(TableHead, { children: "\u0417\u0432\u043E\u043D\u043E\u043A" }), _jsx(TableHead, { children: "\u0420\u0430\u0441\u0448\u0438\u0444\u0440\u043E\u0432\u043A\u0430" }), _jsx(TableHead, { children: "\u0422\u0435\u043A\u0441\u0442" })] }) }), _jsx(TableBody, { children: rows.map((item) => {
                                const fixture = isFixtureTranscriptionText(item.text);
                                const duration = durationLabel(item.durationSec);
                                const call = callParts(item);
                                return (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Link, { to: `/agents/${item.agentId}`, className: "font-medium text-primary hover:underline", children: item.agentDisplayName }) }), _jsx(TableCell, { children: _jsx(Badge, { tone: statusTone(item, fixture), children: transcriptionStatusLabel(fixture ? 'queued' : item.status) }) }), _jsx(TableCell, { children: call.length > 0 ? call.join(' · ') : '—' }), _jsxs(TableCell, { className: "whitespace-nowrap", children: [formatWhen(item.updatedAt), duration ? (_jsx("div", { className: "text-[11px] text-muted-foreground", children: duration })) : null] }), _jsx(TableCell, { className: "min-w-[12rem] text-sm", children: _jsx(TranscriptionText, { item: item }) })] }, item.jobId));
                            }) })] })) })] }));
}
